import { Download, Edit2, Eye, Plus, UserIcon } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import DashboardFilterBar, {
  ALL_OPTION,
  isAllFilter,
  stringFromFilter,
} from "../../components/ui/dashboard/DashboardFilterBar";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { usePermissions } from "../../hooks/usePermissions";
import { buildApiUrl, formatDate } from "../../lib/utils";
import {
  Maintenance,
  MaintenanceSearchRequest,
  MaintenanceSearchResponse,
} from "./@types";
import { useGetMaintenancesMutation } from "./maintenanceApiSlice";
import MaintenanceModal from "./MaintenanceModal";
import PartnerDashStats from "./refactor/MaintenanceDashStats";

const MAINTENANCE_STATUS_OPTIONS = [
  ALL_OPTION,
  { value: "Pending", label: "Pending" },
  { value: "InProgress", label: "In progress" },
  { value: "Completed", label: "Completed" },
];

const MaintenanceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canExport } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedMaintenance, setSelectedMaintenance] =
    useState<Maintenance | null>(null);
  const [openModal, setOpenModal] = useState("");
  const debouncedKeyword = useDebouncedValue(searchTerm.trim());

  const [getMaintenance, { data: maintenanceData, isLoading, error: loadError }] =
    useGetMaintenancesMutation();

  const fetchMaintenance = useCallback(async () => {
    const requestBody: MaintenanceSearchRequest = {
      pageNumber: currentPage,
      pageSize,
      keyword: debouncedKeyword || undefined,
      status: stringFromFilter(statusFilter),
    };

    try {
      await getMaintenance(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch maintenances:", err);
    }
  }, [currentPage, pageSize, debouncedKeyword, statusFilter, getMaintenance]);

  useEffect(() => {
    fetchMaintenance();
  }, [fetchMaintenance]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedKeyword, statusFilter]);

  // Table columns configuration
  const columns: Column<Maintenance>[] = [
    {
      key: "cardCode",
      header: "Card ID",
      width: "24",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "driverName",
      header: "Driver",
      width: "",
      render: (value: string, item: Maintenance) => (
        <div className="flex items-center space-x-3">
          {item.driverImage ? (
            <img
              src={buildApiUrl(item.driverImage)}
              alt={`${item.driverName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.driver}</span>
          </div>
        </div>
      ),
    },

    {
      key: "vehicle",
      header: "Vehicle",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "maintenanceType",
      header: "Maintenance type",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "odometerReading",
      header: "Odometer reading",
      width: "16",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "20",
      render: (value: boolean, item: Maintenance) => {
        const getStatusInfo = () => {
          if (!value)
            return {
              label: "Inactive",
              color: "text-gray-200 border border-gray-200",
            };
          if (item.status === "Completed")
            return {
              label: "Completed",
              color: "text-green-800 border border-green-800",
            };
          if (item.status === "Pending")
            return {
              label: "Pending",
              color: "text-yellow-800 border border-yellow-800",
            };
          if (item.status === "InProgress")
            return {
              label: "InProgress",
              color: "text-blue-800 border border-blue-800",
            };
          return {
            label: "Completed",
            color: "text-green-800 border border-green-800",
          };
        };

        const status = getStatusInfo();
        return (
          <span
            className={`px-2 py-1 inline-flex items-center justify-center text-xs font-semibold rounded-full ${status.color}`}
          >
            {status.label}
          </span>
        );
      },
    },
    {
      key: "createdOn",
      header: "Date",
      width: "20",
      render: (value: string) => (
        <span className="text-hColor">{formatDate(value)}</span>
      ),
    },
    {
      key: "actions",
      header: "Action",
      width: "12",
      align: "center" as const,
      render: (value: any, item: Maintenance) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "View",
                onClick: () => navigate(`/admin/maintenance/${item?.id}`),
                icon: <Eye className="w-4 h-4" />,
              },
              ...(canUpdate("Maintenance")
                ? [
                    {
                      label: "Edit",
                      onClick: () => {
                        setSelectedMaintenance(item);
                        setOpenModal("edit");
                      },
                      icon: <Edit2 className="w-4 h-4" />,
                    },
                  ]
                : []),
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleExportCSV = () => {
    console.log("Export CSV functionality");
  };

  const handleModal = () => {
    setOpenModal("");
    fetchMaintenance();
  };

  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (debouncedKeyword) {
      chips.push({
        key: "keyword",
        label: `Search: ${debouncedKeyword}`,
        onRemove: () => setSearchTerm(""),
      });
    }
    if (!isAllFilter(statusFilter)) {
      chips.push({
        key: "status",
        label: `Status: ${MAINTENANCE_STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label}`,
        onRemove: () => setStatusFilter("all"),
      });
    }
    return chips;
  }, [debouncedKeyword, statusFilter]);

  const response = maintenanceData?.data as
    | MaintenanceSearchResponse
    | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Dashboard Cards */}
        <div className="">
          <PartnerDashStats />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">
              All maintenances
            </h2>
          </div>

          <div className="flex gap-3">
            {canExport("Maintenance") && (
              <AppButton
                appearance="soft"
                onClick={handleExportCSV}
                className="flex items-center px-4 py-2 border border-primary bg-white rounded-lg text-primary hover:bg-gray-50 transition-colors min-w-[146px] h-[38px]"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </AppButton>
            )}
            {canCreate("Maintenance") && (
              <AppButton
                onClick={() => setOpenModal("add")}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors h-[38px]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create
              </AppButton>
            )}
          </div>
        </div>

        {/* All maintenances section */}
        <div className="space-y-4 bg-white rounded-[10px] p-4">
          <DashboardFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onClearSearch={() => setSearchTerm("")}
            searchPlaceholder="Search by vehicle, driver or card ID"
            isLoading={isLoading}
            filters={[
              {
                id: "status",
                placeholder: "Status",
                value: statusFilter,
                options: MAINTENANCE_STATUS_OPTIONS,
                onChange: (value) => setStatusFilter(value || "all"),
              },
            ]}
            chips={activeFilters}
            onClearAll={() => {
              setSearchTerm("");
              setStatusFilter("all");
            }}
            resultCount={response?.totalCount}
            resultLabel={
              response?.totalCount === 1 ? "maintenance" : "maintenances"
            }
          />

          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              error={loadError}
              emptyMessage="No maintenances found"
            />

            {/* Pagination */}
            {response && (
              <Pagination
                currentPage={response.currentPage}
                totalPages={response.totalPages}
                pageSize={response.pageSize}
                totalCount={response.totalCount}
                hasNextPage={response.hasNextPage}
                hasPreviousPage={response.hasPreviousPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        </div>
      </div>

      <MaintenanceModal
        isOpen={openModal === "add" || openModal === "edit"}
        onClose={() => handleModal()}
        maintenance={selectedMaintenance ? selectedMaintenance : null}
      />
    </div>
  );
};

export default MaintenanceDashboard;
